import { createFileRoute, Link } from "@tanstack/react-router";
import { Coins, Eye, Gavel, Zap } from "lucide-react";
import { PublicNav } from "@/components/site/PublicNav";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/partner-with-shields")({
  head: () => ({
    meta: [
      { title: "Partner with Shields — Grow your trade or services business" },
      {
        name: "description",
        content:
          "Join Shields as a verified partner. Bid for free, use Get Now, pay no commission, and buy or earn Shields Credits.",
      },
      { property: "og:title", content: "Partner with Shields" },
      { property: "og:description", content: "No commission. Free bids. Win local work with Shields Credits." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PartnerPitchPage,
});

function PartnerPitchPage() {
  return (
    <div className="min-h-screen">
      <PublicNav />
      <main className="section-pad mx-auto max-w-4xl py-14">
        <h1 className="text-3xl font-bold sm:text-4xl">Partner with Shields</h1>
        <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
          Win local jobs without commission — from handyman work and plumbing to gardening and
          cleaning. You keep the price you agree with the customer, in full, in pounds.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {[
            {
              icon: Gavel,
              title: "Bidding is free",
              body: "Submitting a bid costs 0 Shields Credits. Credits are only used if the customer chooses your bid.",
            },
            {
              icon: Zap,
              title: "Get Now",
              body: "See a job you want? Take it instantly for a fixed Shields Credit price shown before you confirm. First valid claim wins — losers are never charged.",
            },
            {
              icon: Coins,
              title: "Shields Credits",
              body: "Buy credit packs in pounds or earn free credits through verified achievements. Credits have no cash value and can't be transferred or withdrawn.",
            },
            {
              icon: Eye,
              title: "Privacy-safe offers",
              body: "You see the scope, condition, size, area and access notes before committing — never the customer's personal details until you're assigned.",
            },
          ].map((f) => (
            <div key={f.title} className="card-surface p-6">
              <f.icon className="h-7 w-7 text-primary" aria-hidden />
              <h2 className="mt-3 font-display font-bold">{f.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="card-surface mt-10 p-6">
          <h2 className="font-display text-lg font-bold">How approval works</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            <li>Apply with your trading details, services, experience and insurance.</li>
            <li>A member of the Shields team reviews every application by hand — nothing is approved or rejected automatically.</li>
            <li>Once approved, your public profile goes live and matching job offers arrive in your inbox.</li>
          </ol>
        </div>

        <div className="mt-10 text-center">
          <Link to="/partner/apply" className="btn-hero">
            Apply to become a Partner
          </Link>
          <p className="mt-3 text-xs text-muted-foreground">
            You'll need a free Shields account first. Approval is a human decision and typically
            takes 1–2 working days.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
